
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { ProfessionalPatientAccess } from "@/types/access.types";
import { Check, X, Trash } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";

export const AccessRequestsList = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  // Récupérer les demandes d'accès
  const { data: accessRequests, isLoading } = useQuery({
    queryKey: ["access-requests"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const { data, error } = await supabase
        .from("professional_patient_access")
        .select(`
          *,
          professional:profiles!professional_id(*)
        `)
        .eq("patient_id", user.id)
        .eq("status", "pending");

      if (error) {
        console.error("Erreur lors de la récupération des demandes d'accès:", error);
        toast({
          variant: "destructive",
          title: "Erreur",
          description: "Impossible de charger les demandes d'accès",
        });
        return [];
      }

      return data as (ProfessionalPatientAccess & { professional: any })[];
    },
  });

  // Mutation pour mettre à jour le statut d'une demande
  const updateAccessMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: 'approved' | 'rejected' }) => {
      const { error } = await supabase
        .from("professional_patient_access")
        .update({ status })
        .eq("id", id);

      if (error) throw error;

      // Si la demande est approuvée, mettre également à jour professional_patients
      if (status === 'approved') {
        const { data: accessData } = await supabase
          .from("professional_patient_access")
          .select("professional_id, patient_id")
          .eq("id", id)
          .single();

        if (accessData) {
          const { error: relationError } = await supabase
            .from("professional_patients")
            .update({ status: 'approved' })
            .eq("professional_id", accessData.professional_id)
            .eq("patient_id", accessData.patient_id);

          if (relationError) throw relationError;

          // Créer une notification pour le professionnel
          const { error: notifError } = await supabase
            .from("notifications")
            .insert({
              user_id: accessData.professional_id,
              title: "Demande d'accès acceptée",
              content: "Un patient a accepté votre demande d'accès à ses données médicales.",
              type: "access_approved"
            });

          if (notifError) console.error("Erreur lors de la création de la notification:", notifError);
        }
      } else if (status === 'rejected') {
        // Si la demande est rejetée, notification au professionnel
        const { data: accessData } = await supabase
          .from("professional_patient_access")
          .select("professional_id, patient_id")
          .eq("id", id)
          .single();

        if (accessData) {
          const { error: notifError } = await supabase
            .from("notifications")
            .insert({
              user_id: accessData.professional_id,
              title: "Demande d'accès refusée",
              content: "Un patient a refusé votre demande d'accès à ses données médicales.",
              type: "access_rejected"
            });

          if (notifError) console.error("Erreur lors de la création de la notification:", notifError);
        }
      }

      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["access-requests"] });
      toast({
        title: "Succès",
        description: "La demande d'accès a été mise à jour",
      });
    },
    onError: (error) => {
      console.error("Erreur lors de la mise à jour de la demande:", error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de mettre à jour la demande d'accès",
      });
    },
  });

  // Mutation pour supprimer toutes les demandes
  const deleteAllAccessMutation = useMutation({
    mutationFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Utilisateur non authentifié");

      setIsBulkDeleting(true);

      // Supprimer toutes les demandes d'accès en attente
      const { error } = await supabase
        .from("professional_patient_access")
        .delete()
        .eq("patient_id", user.id)
        .eq("status", "pending");

      if (error) throw error;

      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["access-requests"] });
      toast({
        title: "Succès",
        description: "Toutes les demandes d'accès ont été supprimées",
      });
      setIsBulkDeleting(false);
    },
    onError: (error) => {
      console.error("Erreur lors de la suppression des demandes:", error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de supprimer les demandes d'accès",
      });
      setIsBulkDeleting(false);
    },
  });

  // Gestion des actions
  const handleAccept = (id: string) => {
    updateAccessMutation.mutate({ id, status: 'approved' });
  };

  const handleReject = (id: string) => {
    updateAccessMutation.mutate({ id, status: 'rejected' });
  };

  const handleDeleteAll = () => {
    if (window.confirm("Êtes-vous sûr de vouloir supprimer toutes les demandes d'accès en attente?")) {
      deleteAllAccessMutation.mutate();
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (!accessRequests || accessRequests.length === 0) {
    return (
      <Alert className="bg-muted">
        <AlertDescription>
          Aucune demande d'accès en attente.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Demandes d'accès en attente</h3>
        <Button 
          variant="destructive" 
          size="sm" 
          onClick={handleDeleteAll}
          disabled={isBulkDeleting || updateAccessMutation.isPending}
          className="flex items-center gap-2"
        >
          <Trash className="h-4 w-4" />
          Supprimer tout
        </Button>
      </div>

      <div className="space-y-4">
        {accessRequests.map((request) => (
          <Card key={request.id} className="relative overflow-hidden">
            {request.professional?.profile_photo && (
              <div className="absolute right-0 top-0 h-20 w-20 overflow-hidden opacity-10">
                <img 
                  src={request.professional.profile_photo} 
                  alt="" 
                  className="h-full w-full object-cover"
                />
              </div>
            )}
            <CardHeader>
              <CardTitle>{request.professional?.full_name || "Professionnel de santé"}</CardTitle>
              <CardDescription>
                {request.professional?.email || "Email non disponible"}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="mb-4">
                <p>Demande d'accès à vos données médicales</p>
                <ul className="list-disc list-inside mt-2 text-sm text-muted-foreground">
                  {request.medications_access && <li>Accès à votre liste de médicaments</li>}
                  {request.journal_access && <li>Accès à votre journal de santé</li>}
                  {request.mood_access && <li>Accès à vos données d'humeur</li>}
                </ul>
              </div>
              <div className="flex gap-2 mt-4">
                <Button 
                  onClick={() => handleAccept(request.id)}
                  disabled={updateAccessMutation.isPending}
                  variant="default"
                  className="flex items-center gap-2"
                >
                  <Check className="h-4 w-4" />
                  Accepter
                </Button>
                <Button 
                  onClick={() => handleReject(request.id)}
                  disabled={updateAccessMutation.isPending}
                  variant="destructive"
                  className="flex items-center gap-2"
                >
                  <X className="h-4 w-4" />
                  Refuser
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};
